import { useState, useEffect } from 'react'
import { TrendingUp, Users, Target, AlertCircle, Info, Download, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react'
import { Button, Modal, Box, Typography, CircularProgress } from '@mui/material'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorMessage from '../ui/ErrorMessage'
import MetricCard from '../ui/MetricCard'
import DataTable from '../ui/DataTable'
import { apiService } from '../../services/api'
import { debug } from '../../utils/debug'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'

const VisitedSalonsSales = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [salesData, setSalesData] = useState(null)
  const [operatorsData, setOperatorsData] = useState([])
  const [operatorsLoaded, setOperatorsLoaded] = useState(false)
  
  // Filters
  const [periodDays, setPeriodDays] = useState(7)
  const [daysBack, setDaysBack] = useState(30)
  const [operatorFilter, setOperatorFilter] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showThresholds, setShowThresholds] = useState(false)
  
  // Modal state
  const [selectedKamOperator, setSelectedKamOperator] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [kamSalonsData, setKamSalonsData] = useState([])
  
  // Sorting state
  const [summarySortModel, setSummarySortModel] = useState([{ field: 'total_sales', sort: 'desc' }])
  const [detailsSortModel, setDetailsSortModel] = useState([{ field: 'sales_amount', sort: 'desc' }])
  
  // Modal sorting state
  const [modalSortModel, setModalSortModel] = useState([{ field: 'sales', sort: 'desc' }])

  debug.component('VisitedSalonsSales', 'Component initialized')

  useEffect(() => {
    fetchOperators()
  }, [])

  useEffect(() => {
    if (operatorsLoaded) {
      fetchData()
    }
  }, [periodDays, daysBack, operatorFilter, operatorsLoaded])

  const fetchOperators = async () => {
    try {
      const response = await apiService.getVisitOperators()
      setOperatorsData(response.data || [])
      setOperatorsLoaded(true)
      debug.log('Operators data fetched', response.data, 'VisitedSalonsSales')
    } catch (err) {
      debug.error('Error fetching operators', err, 'VisitedSalonsSales')
      setOperatorsLoaded(true)
    }
  }

  const fetchData = async () => {
    debug.log('Fetching visited salons sales data', { periodDays, daysBack, operatorFilter }, 'VisitedSalonsSales')
    setLoading(true)
    setError(null)

    try {
      const params = {
        period_days: periodDays,
        days_back: daysBack
      }
      if (operatorFilter) {
        params.operator_id = operatorFilter
      }

      const response = await apiService.getVisitedSalonsSales(params)
      setSalesData(response.data)
      debug.log('Sales data fetched successfully', response.data, 'VisitedSalonsSales')
    } catch (err) {
      debug.error('Error fetching visited salons sales', err, 'VisitedSalonsSales')
      setError('Nie udało się załadować danych sprzedaży odwiedzonych salonów.')
    } finally {
      setLoading(false)
    }
  }

  const handleKamSalonsDetails = async (kamName, operatorName) => {
    try {
      setSelectedKamOperator({ kamName, operatorName })
      setModalOpen(true)
      
      // Find KAM ID and Operator ID from summary data
      const summaryItem = summary.find(item => 
        item.kam_name === kamName && item.operator_name === operatorName
      )
      
      if (!summaryItem) {
        console.error('Could not find KAM/Operator IDs for:', kamName, operatorName)
        setKamSalonsData([])
        return
      }
      
      // Get detailed data for this specific KAM/operator combination
      const params = {
        period_days: periodDays,
        days_back: daysBack,
        kam_id: summaryItem.kam_id,
        operator_id: summaryItem.operator_id
      }
      
      console.log('Fetching KAM salons details:', params)
      const response = await apiService.getVisitedSalonsSales(params)
      setKamSalonsData(response.data?.details || [])
    } catch (err) {
      console.error('Error fetching KAM salons details:', err)
      setKamSalonsData([])
    }
  }

  // Efficiency thresholds based on period
  const getEfficiencyThresholds = (periodDays) => {
    if (periodDays <= 7) {
      return { bad: 0.50, medium: 0.75, good: 0.75 }
    } else if (periodDays <= 14) {
      return { bad: 0.75, medium: 1.00, good: 1.00 }
    } else {
      return { bad: 1.00, medium: 1.20, good: 1.50 }
    }
  }

  const getEfficiencyColor = (value, periodDays) => {
    const { bad, medium, good } = getEfficiencyThresholds(periodDays)
    
    if (periodDays <= 7) {
      if (value < bad) return "🔴"
      else if (value < medium) return "🟡"
      else return "🟢"
    } else if (periodDays <= 14) {
      if (value < bad) return "🔴"
      else if (value < medium) return "🟡"
      else return "🟢"
    } else {
      if (value < bad) return "🔴"
      else if (value < medium) return "🟡"
      else if (value < good) return "🟢"
      else return "🌟"
    }
  }

  const formatPeriodText = (days) => {
    if (days < 30) return `${days} dni`
    return "Miesiąc"
  }

  const operatorOptions = [
    ...(operatorsData.length > 1 ? [{ id: null, name: "Wszystkie" }] : []),
    ...operatorsData.map(op => ({ id: op.id, name: op.name }))
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        onRetry={fetchData}
        showRetry={true}
      />
    )
  }

  const summary = salesData?.summary || []
  const details = salesData?.details || []
  const totalSales = salesData?.total_sales || 0
  const totalOperatorSales = salesData?.total_operator_sales || {}
  const totalOperatorSalesSum = salesData?.total_operator_sales_sum || 0

  const totalKams = summary.length > 0 ? new Set(summary.map(item => item.kam_name)).size : 0
  const totalSalons = details.length > 0 ? new Set(details.map(item => item.salon_id)).size : 0
  const totalVisits = summary.reduce((sum, item) => sum + (item.visit_count || 0), 0)
  const coveragePercent = totalOperatorSalesSum > 0 ? (totalSales / totalOperatorSalesSum) * 100 : 0

  return (
    <div className="space-y-6">
      {/* Efficiency Thresholds Info - Collapsible */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg">
        <div 
          className="p-4 cursor-pointer hover:bg-blue-100 transition-colors duration-200"
          onClick={() => setShowThresholds(!showThresholds)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-blue-900">ℹ️ Progi efektywności</h4>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-700">
                {showThresholds ? 'Zwiń' : 'Rozwiń'}
              </span>
              {showThresholds ? (
                <ChevronUp className="h-4 w-4 text-blue-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-blue-600" />
              )}
            </div>
          </div>
        </div>
        
        {showThresholds && (
          <div className="px-4 pb-4 border-t border-blue-200 bg-blue-50">
            <div className="pt-3 text-sm text-blue-800 space-y-2">
              <div>
                <div className="font-semibold mb-1">📅 Tydzień (7 dni):</div>
                <div className="ml-4">
                  🔴 <strong>Źle:</strong> &lt; 50% (0.50 szt/wizytę)<br />
                  🟡 <strong>Średnio:</strong> 50% - 74% (0.50 - 0.74 szt/wizytę)<br />
                  🟢 <strong>Dobrze:</strong> ≥ 75% (≥ 0.75 szt/wizytę)
                </div>
              </div>
              
              <div>
                <div className="font-semibold mb-1">📅 2 tygodnie (14 dni):</div>
                <div className="ml-4">
                  🔴 <strong>Źle:</strong> &lt; 75% (0.75 szt/wizytę)<br />
                  🟡 <strong>Średnio:</strong> 75% - 99% (0.75 - 0.99 szt/wizytę)<br />
                  🟢 <strong>Dobrze:</strong> ≥ 100% (≥ 1.00 szt/wizytę)
                </div>
              </div>
              
              <div>
                <div className="font-semibold mb-1">📅 Miesiąc (30 dni):</div>
                <div className="ml-4">
                  🔴 <strong>Źle:</strong> &lt; 100% (1.00 szt/wizytę)<br />
                  🟡 <strong>Średnio:</strong> 100% - 119% (1.00 - 1.19 szt/wizytę)<br />
                  🟢 <strong>Dobrze:</strong> 120% - 149% (1.20 - 1.49 szt/wizytę)<br />
                  🌟 <strong>Świetnie:</strong> ≥ 150% (≥ 1.50 szt/wizytę)
                </div>
              </div>
              
              <div className="mt-3 pt-2 border-t border-blue-300">
                <div className="text-xs text-blue-600 italic">
                  💡 <strong>Wskazówka:</strong> Progi są zależne od wybranego okresu sprzedaży. 
                  Dłuższy okres = wyższe oczekiwania efektywności.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Okres sprzedaży</label>
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>7 dni</option>
            <option value={14}>14 dni</option>
            <option value={30}>30 dni</option>
            <option value={60}>60 dni</option>
            <option value={90}>90 dni</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Okres wizyt</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>Ostatnie 7 dni</option>
            <option value={14}>Ostatnie 14 dni</option>
            <option value={30}>Ostatnie 30 dni</option>
            <option value={60}>Ostatnie 60 dni</option>
            <option value={90}>Ostatnie 90 dni</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
          <select
            value={operatorFilter || ''}
            onChange={(e) => setOperatorFilter(e.target.value ? Number(e.target.value) : null)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            {operatorOptions.map(option => (
              <option key={option.id || 'all'} value={option.id || ''}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Opcje</label>
          <div className="flex items-center space-x-2 mt-2">
            <input
              type="checkbox"
              id="showDetails"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="showDetails" className="text-sm text-gray-700">
              Pokaż szczegóły
            </label>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Sprzedaż (odwiedzone)"
          value={totalSales.toLocaleString()}
          icon={TrendingUp}
        />
        <MetricCard
          title="Liczba KAM"
          value={totalKams}
          icon={Users}
        />
        <MetricCard
          title="Odwiedzone salony"
          value={totalSalons}
          icon={Target}
        />
        <MetricCard
          title="Liczba wizyt"
          value={totalVisits}
          icon={AlertCircle}
        />
      </div>

      {/* Operator Sales & Coverage - per operator cards when no filter */}
      {!operatorFilter && Object.keys(totalOperatorSales).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-600">
            📊 Sprzedaż per operator (szt.) — pokrycie wizytami w wybranym okresie
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(totalOperatorSales).map(([operatorName, operatorTotal]) => {
            // Get operator sales from visited salons
            const operatorVisitedSales = summary
              .filter(s => s.operator_name === operatorName)
              .reduce((sum, s) => sum + (s.total_sales || 0), 0)
            const opCoverage = operatorTotal > 0 ? (operatorVisitedSales / operatorTotal) * 100 : 0
            
            // Get operator color
            const colorKey = operatorName.toLowerCase()
            const colors = {
              'orange': { primary: '#FF7900', light: 'rgba(255, 121, 0, 0.1)' },
              'plus': { primary: '#00A651', light: 'rgba(0, 166, 81, 0.1)' },
              'play': { primary: '#9333EA', light: 'rgba(147, 51, 234, 0.1)' },
              't-mobile': { primary: '#E20074', light: 'rgba(226, 0, 116, 0.1)' },
              'tmobile': { primary: '#E20074', light: 'rgba(226, 0, 116, 0.1)' }
            }
            const opColor = colors[colorKey] || { primary: '#6B7280', light: 'rgba(107, 114, 128, 0.1)' }
            
            return (
              <div 
                key={operatorName}
                className="rounded-lg border p-3 transition-shadow hover:shadow-md"
                style={{ 
                  borderColor: opColor.primary,
                  backgroundColor: opColor.light
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span 
                    className="font-semibold text-sm"
                    style={{ color: opColor.primary }}
                  >
                    {operatorName}
                  </span>
                  <span 
                    className="text-xs font-medium px-2 py-0.5 rounded"
                    style={{ 
                      backgroundColor: opColor.primary,
                      color: 'white'
                    }}
                  >
                    {opCoverage.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <div>
                    <span className="text-gray-500">Sprzedaż (odwiedzone): </span>
                    <span className="font-medium">{operatorVisitedSales} szt.</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Sprzedaż (ogółem): </span>
                    <span className="font-medium">{operatorTotal} szt.</span>
                  </div>
                </div>
              </div>
            )
          })}
          </div>
        </div>
      )}

      {/* Single operator view - show single card */}
      {operatorFilter && totalOperatorSalesSum > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Sprzedaż (operator)"
            value={totalOperatorSalesSum.toLocaleString()}
            icon={BarChart3}
            trend={Object.entries(totalOperatorSales).map(([name, val]) => `${name}: ${val}`).join(', ')}
          />
          <MetricCard
            title="Pokrycie wizytami"
            value={`${coveragePercent.toFixed(1)}%`}
            icon={Target}
            trend={`${totalSales} z ${totalOperatorSalesSum}`}
          />
        </div>
      )}

      {/* Summary Table by KAM and Operator */}
      {summary.length > 0 && (
        <div className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">📊 Podsumowanie per KAM i Operator</h3>
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Sprzedaż:</strong> Suma sprzedaży ze wszystkich salonów odwiedzonych przez KAM-a w ostatnich {daysBack} dniach (okres: {periodDays} dni).
                <br />
                <strong>Efektywność:</strong> Średnia sprzedaż na wizytę (sprzedaż ÷ wizyty). Wysoka wartość = KAM odwiedza rentowne salony.
                <br />
                <strong>Status:</strong> Ocena jakości wyboru salonów na podstawie progów efektywności dla {periodDays}-dniowego okresu.
              </p>
            </div>
          </div>
          
          <DataTable
            rows={summary.map((item, index) => {
              const totalSales = Number(item.total_sales) || 0
              const visitCount = Number(item.visit_count) || 0
              const salonCount = Number(item.salon_count) || 0
              const efficiency = visitCount > 0 ? totalSales / visitCount : 0
              const status = getEfficiencyColor(efficiency, periodDays)
              
              
              return {
                id: index,
                kam_name: item.kam_name || '',
                operator_name: item.operator_name || '',
                total_sales: totalSales,
                salon_count: salonCount,
                visit_count: visitCount,
                efficiency: efficiency, // Keep original for now
                status: status,
              }
            })}
            columns={[
              {
                field: 'kam_name',
                headerName: 'KAM',
                flex: 2,
                minWidth: 120,
                type: 'string',
              },
              {
                field: 'operator_name',
                headerName: 'Operator',
                flex: 1.5,
                minWidth: 100,
                type: 'string',
              },
              {
                field: 'total_sales',
                headerName: 'Sprzedaż',
                flex: 1,
                minWidth: 100,
                type: 'number',
                renderCell: (params) => <span>{params.value ?? 0}</span>,
              },
              {
                field: 'salon_count',
                headerName: 'Salony',
                flex: 1,
                minWidth: 80,
                type: 'number',
              },
              {
                field: 'visit_count',
                headerName: 'Wizyty',
                flex: 1,
                minWidth: 80,
                type: 'number',
              },
              {
                field: 'efficiency',
                headerName: 'Efektywność',
                flex: 1.2,
                minWidth: 110,
                type: 'number',
                renderCell: (params) => <span>{(params.value ?? 0).toFixed(3)}</span>,
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 80,
                type: 'string',
                sortable: false,
                filterable: false,
              },
              {
                field: 'actions',
                headerName: 'Akcje',
                width: 120,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleKamSalonsDetails(params.row.kam_name, params.row.operator_name)}
                    style={{ fontSize: '0.75rem' }}
                  >
                    Szczegóły
                  </Button>
                ),
              },
            ]}
            pageSize={10}
            autoHeight
            sortModel={summarySortModel}
            onSortModelChange={(model) => setSummarySortModel(model)}
          />
        </div>
      )}

      {/* Chart */}
      {summary.length > 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">📈 Wykres Sprzedaży per KAM</h3>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="w-full h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summary
                    .sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0))
                    .map(item => ({
                      name: `${item.kam_name} (${item.operator_name})`,
                      sprzedaz: item.total_sales || 0,
                      efektywnosc: item.visit_count > 0 ? ((item.total_sales || 0) / item.visit_count) : 0,
                      salony: item.salon_count || 0,
                      wizyty: item.visit_count || 0,
                    }))
                  }
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis />
                  <RechartsTooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null
                      const data = payload[0]?.payload
                      return (
                        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
                          <p className="font-semibold text-gray-900 mb-2">{label}</p>
                          <div className="space-y-1 text-sm">
                            <p><span className="text-blue-600">●</span> Sprzedaż: <strong>{data?.sprzedaz?.toLocaleString() || 0} szt.</strong></p>
                            <p><span className="text-green-600">●</span> Efektywność: <strong>{(data?.efektywnosc || 0).toFixed(2)} szt./wizytę</strong></p>
                            <p className="text-gray-500 border-t pt-1 mt-1">Wizyty: {data?.wizyty || 0} | Salony: {data?.salony || 0}</p>
                          </div>
                        </div>
                      )
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sprzedaz" fill="#3b82f6" name="Sprzedaż (szt.)" />
                  <Bar dataKey="efektywnosc" fill="#10b981" name="Efektywność (szt./wizytę)" yAxisId="right" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Salon Data */}
      {showDetails && details.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">🏢 Szczegóły per Salon</h3>
            <button
              onClick={() => {
                // Simple CSV export functionality
                const csvContent = [
                  ['KAM', 'Operator', 'Salon', 'Plant Code', 'Wizyty', 'Ostatnia wizyta', 'Sprzedaż', 'Efektywność', 'Status'],
                  ...details.map(item => {
                    const efficiency = item.visit_count > 0 ? (item.sales_amount || 0) / item.visit_count : 0
                    const status = getEfficiencyColor(efficiency, periodDays)
                    return [
                      item.kam_name,
                      item.operator_name,
                      item.salon_name,
                      item.plant_code,
                      item.visit_count,
                      item.last_visit_date?.substring(0, 10) || '',
                      item.sales_amount || 0,
                      efficiency.toFixed(2),
                      status
                    ]
                  })
                ].map(row => row.join(',')).join('\n')
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
                const link = document.createElement('a')
                const url = URL.createObjectURL(blob)
                link.setAttribute('href', url)
                link.setAttribute('download', `sprzedaz_odwiedzone_salony_${periodDays}d.csv`)
                link.style.visibility = 'hidden'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Download className="h-4 w-4" />
              <span>📥 Pobierz CSV</span>
            </button>
          </div>

          <DataTable
            rows={details.map((item, index) => {
              const salesAmount = Number(item.sales_amount) || 0
              const visitCount = Number(item.visit_count) || 0
              const efficiency = visitCount > 0 ? salesAmount / visitCount : 0
              const status = getEfficiencyColor(efficiency, periodDays)
              
              
              return {
                id: index,
                kam_name: item.kam_name || '',
                operator_name: item.operator_name || '',
                salon_name: item.salon_name || '',
                plant_code: item.plant_code || '',
                visit_count: visitCount,
                last_visit_date: item.last_visit_date?.substring(0, 10) || '—',
                sales_amount: salesAmount,
                efficiency: efficiency,
                status: status,
              }
            })}
            columns={[
              {
                field: 'kam_name',
                headerName: 'KAM',
                flex: 1.5,
                minWidth: 120,
                type: 'string',
              },
              {
                field: 'operator_name',
                headerName: 'Operator',
                flex: 1,
                minWidth: 90,
                type: 'string',
              },
              {
                field: 'salon_name',
                headerName: 'Salon',
                flex: 2.5,
                minWidth: 180,
                type: 'string',
              },
              {
                field: 'plant_code',
                headerName: 'Plant Code',
                flex: 1,
                minWidth: 100,
                type: 'string',
                renderCell: (params) => (
                  <span className="font-mono text-sm">{params.value}</span>
                ),
              },
              {
                field: 'visit_count',
                headerName: 'Wizyty',
                flex: 0.8,
                minWidth: 80,
                type: 'number',
              },
              {
                field: 'last_visit_date',
                headerName: 'Ostatnia wizyta',
                flex: 1.2,
                minWidth: 120,
                type: 'string',
              },
              {
                field: 'sales_amount',
                headerName: 'Sprzedaż',
                flex: 1,
                minWidth: 100,
                type: 'number',
                renderCell: (params) => <span>{params.value ?? 0}</span>,
              },
              {
                field: 'efficiency',
                headerName: 'Efektywność',
                flex: 1.2,
                minWidth: 110,
                type: 'number',
                renderCell: (params) => <span>{(params.value ?? 0).toFixed(3)}</span>,
              },
              {
                field: 'status',
                headerName: 'Status',
                flex: 0.8,
                minWidth: 70,
                type: 'string',
                sortable: false,
                filterable: false,
              },
            ]}
            pageSize={25}
            height={600}
            sortModel={detailsSortModel}
            onSortModelChange={(model) => setDetailsSortModel(model)}
          />
        </div>
      )}

      {/* KAM Details Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="kam-details-modal"
        aria-describedby="kam-details-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            maxWidth: 1200,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
          }}
        >
          {selectedKamOperator && (
            <>
              <Typography variant="h5" component="h2" gutterBottom>
                📊 Szczegóły salonów dla {selectedKamOperator.kamName} ({selectedKamOperator.operatorName})
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Sprzedaż i wizyty w ostatnich {daysBack} dniach (okres: {periodDays} dni)
              </Typography>

              {kamSalonsData.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <DataTable
                    rows={kamSalonsData.map((salon, index) => {
                      const sales = Number(salon.sales_amount || 0)
                      const visits = Number(salon.visit_count || 0)
                      const efficiency = visits > 0 ? sales / visits : 0
                      const status = getEfficiencyColor(efficiency, periodDays)
                      
                      return {
                        id: index,
                        salon_name: salon.salon_name || 'N/A',
                        sales: sales,
                        visits: visits,
                        efficiency: efficiency,
                        status: status,
                        last_visit_date: salon.last_visit_date 
                          ? new Date(salon.last_visit_date).toLocaleDateString('pl-PL')
                          : 'Brak wizyt'
                      }
                    })}
                    columns={[
                      { field: 'salon_name', headerName: 'Salon', flex: 2, minWidth: 200 },
                      { field: 'sales', headerName: 'Sprzedaż', flex: 1, minWidth: 100, type: 'number' },
                      { field: 'visits', headerName: 'Wizyty', flex: 1, minWidth: 80, type: 'number' },
                      { 
                        field: 'efficiency', 
                        headerName: 'Efektywność', 
                        flex: 1, 
                        minWidth: 110, 
                        type: 'number',
                        renderCell: (params) => <span>{(params.value ?? 0).toFixed(3)}</span>
                      },
                      { field: 'status', headerName: 'Status', width: 80, sortable: false },
                      { field: 'last_visit_date', headerName: 'Data ostatniej wizyty', flex: 1.2, minWidth: 150 }
                    ]}
                    pageSize={10}
                    height={400}
                    sortModel={modalSortModel}
                    onSortModelChange={(model) => setModalSortModel(model)}
                    density="compact"
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Ładowanie szczegółów...</Typography>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => setModalOpen(false)} variant="contained">
                  Zamknij
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  )
}

export default VisitedSalonsSales
